<template>
  <div>
    <strip-builder-create
      v-if="currentStep === 'create'"
      ref="createForm"
      @create="onCreateStrip"
    />

    <b-card v-else-if="currentStep === 'done'">
      <h3>Полоска готова!</h3>
      <p>Все 5 вопросов заполнены.</p>
      <div class="d-flex" style="gap: 8px">
        <b-button variant="success" @click="onFinalize">Финализировать</b-button>
        <b-button variant="primary" :to="{ name: 'strip-edit', params: { id: stripId } }">К редактированию</b-button>
        <b-button variant="outline-secondary" :to="{ name: 'strips-list' }">К списку</b-button>
      </div>
    </b-card>

    <div v-else-if="stripData">
      <b-progress :value="progressPercent" :max="100" variant="primary" class="mb-2" style="height: 6px" />
      <strip-builder-question-step
        :strip-id="stripId"
        :question-index="currentQuestionIndex"
        :strip-format="stripData.format"
        :scale-type="stripData.scale_type"
        :base-price="stripData.base_price"
        :is-saving="isSaving"
        @accepted="onQuestionAccepted"
        @accepted-manual="onManualQuestionAccepted"
        @skipped="onQuestionSkipped"
      />
    </div>

    <div v-else class="text-center py-3">
      <b-spinner variant="primary" />
    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BProgress, BSpinner,
} from 'bootstrap-vue'
import {
  ref, computed, onUnmounted,
} from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import router from '@/router'
import store from '@/store'
import stripBuilderStoreModule from './stripBuilderStoreModule'
import StripBuilderCreate from './StripBuilderCreate.vue'
import StripBuilderQuestionStep from './StripBuilderQuestionStep.vue'

export default {
  components: {
    BCard, BButton, BProgress, BSpinner,
    StripBuilderCreate, StripBuilderQuestionStep,
  },
  setup() {
    const STORE_MODULE_NAME = 'strip-builder'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, stripBuilderStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const stripId = ref(router.currentRoute.params.id || null)
    const stripData = ref(null)
    const createForm = ref(null)
    const currentStep = ref('create')
    const isSaving = ref(false)

    const filledOrders = computed(() => {
      if (!stripData.value || !stripData.value.strip_questions) return []
      return stripData.value.strip_questions.map(sq => sq.order)
    })

    const currentQuestionIndex = computed(() => {
      for (let i = 1; i <= 5; i += 1) {
        if (!filledOrders.value.includes(i)) return i
      }
      return 5
    })

    const progressPercent = computed(() => Math.round((filledOrders.value.length / 5) * 100))

    const showToast = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          variant,
        },
      })
    }

    const advanceStep = () => {
      if (!stripData.value) {
        currentStep.value = 'create'
        return
      }
      if (filledOrders.value.length >= 5) {
        currentStep.value = 'done'
      } else {
        currentStep.value = 'question'
      }
    }

    const loadStrip = id => {
      store.dispatch('strip-builder/loadStrip', { id })
        .then(response => {
          stripData.value = response.data
          advanceStep()
        })
        .catch(() => {
          showToast('Ошибка загрузки полоски', 'danger')
        })
    }

    if (stripId.value) loadStrip(stripId.value)

    const onCreateStrip = payload => {
      isSaving.value = true
      store.dispatch('strip-builder/createStrip', payload)
        .then(response => {
          const data = response.data
          isSaving.value = false
          router.replace({ name: 'strip-edit', params: { id: data.id } })
        })
        .catch(error => {
          isSaving.value = false
          if (createForm.value) createForm.value.stopLoading()
          const msg = error.response?.data?.message || 'Ошибка создания полоски'
          showToast(msg, 'danger')
        })
    }

    const onQuestionAccepted = payload => {
      isSaving.value = true
      store.dispatch('strip-builder/acceptQuestion', {
        stripId: stripId.value,
        payload,
      })
        .then(() => {
          isSaving.value = false
          showToast('Вопрос сохранён', 'success')
          loadStrip(stripId.value)
        })
        .catch(error => {
          isSaving.value = false
          const msg = error.response?.data?.message || 'Ошибка сохранения'
          showToast(msg, 'danger')
        })
    }

    const onManualQuestionAccepted = ({ formData }) => {
      isSaving.value = true
      store.dispatch('strip-builder/acceptManualQuestion', {
        stripId: stripId.value,
        formData,
      })
        .then(() => {
          isSaving.value = false
          showToast('Вопрос сохранён', 'success')
          loadStrip(stripId.value)
        })
        .catch(error => {
          isSaving.value = false
          const msg = error.response?.data?.message || 'Ошибка сохранения'
          showToast(msg, 'danger')
        })
    }

    const onQuestionSkipped = () => {
      // remain on the same slot; user can pick again
    }

    const onFinalize = () => {
      store.dispatch('strip-builder/finalizeStrip', { id: stripId.value })
        .then(response => {
          if (response.data.success) {
            showToast('Полоска финализирована', 'success')
            router.push({ name: 'strips-list' })
          } else {
            showToast(response.data.errors?.[0] || 'Ошибка', 'danger')
          }
        })
        .catch(() => showToast('Ошибка финализации', 'danger'))
    }

    return {
      stripId, stripData, currentStep, isSaving, createForm,
      progressPercent, currentQuestionIndex,
      onCreateStrip, onQuestionAccepted, onManualQuestionAccepted, onQuestionSkipped, onFinalize,
    }
  },
}
</script>
